import React, { useEffect, useRef, useMemo } from 'react';
import { 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Paper,
  Typography,
  Box,
  Divider,
  Autocomplete,
  TextField
} from '@mui/material';
import { SchoolOutlined } from '@mui/icons-material';
import { formatSchoolName, capitalizeWord } from '../../utils/nameFormatter';
import { getSchoolGrades } from '../../utils/schoolUtils';
import { getTravelModeOptions } from '../../constants/travelModes';
import { renderSchoolOption, filterSchoolOptions } from './schoolOption';

// Define grades in the order we want them displayed, excluding Pre-K
const GRADES = [
  'K',  // Changed from 'Kindergarten'
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'
];

const DRIVE_TIMES = [5, 10, 15, 20, 25, 30];

// Function to format school type labels
const formatSchoolType = (type) => {
  if (!type || type === 'All') return type;
  
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DEFAULT_GOVERNANCE_OPTIONS = ['Charter', 'District', 'Private'];

const SharedFilters = ({
  schools,
  selectedSchool,
  governanceFilter,
  selectedGrades = [],
  selectedDriveTime,
  selectedTravelMode = 'drive',
  schoolTypeFilter,
  availableSchoolTypes,
  availableDriveTimes = null,
  availableTravelModes = null,
  onSchoolChange,
  onGovernanceChange,
  onGradeChange,
  onDriveTimeChange,
  onTravelModeChange,
  onSchoolTypeChange,
  onHasPreKChange,
  showSchoolType = false,
  /** When set, only these governance values appear in the dropdown (e.g. Charter + District only). */
  governanceOptions = DEFAULT_GOVERNANCE_OPTIONS,
  compact = false,
  sticky = false
}) => {
  const driveTimeOptions = availableDriveTimes || DRIVE_TIMES;
  const travelModeOptions = getTravelModeOptions(availableTravelModes);
  // Use ref to track if the initial school grades have been set
  const initialGradesSet = useRef(false);

  // Sort schools once and memoize the result
  const sortedSchools = useMemo(() => {
    return [...schools].sort((a, b) => 
      formatSchoolName(a.name).localeCompare(formatSchoolName(b.name))
    );
  }, [schools]);

  // Effect to set initial grades ONLY when school first changes
  useEffect(() => {
    if (selectedSchool?.grade_range && !initialGradesSet.current) {
      const schoolGrades = getSchoolGrades(selectedSchool.grade_range);
      const hasPreK = selectedSchool.grade_range.includes('Pre-Kindergarten');
      
      onHasPreKChange?.(hasPreK);
      onGradeChange({ target: { value: schoolGrades } });
      initialGradesSet.current = true;
    } else if (!selectedSchool) {
      initialGradesSet.current = false;
      onGradeChange({ target: { value: [] } });
      onHasPreKChange?.(false);
    } else if (selectedSchool && !selectedSchool.grade_range && !initialGradesSet.current) {
      // For drop-a-pin locations, let Geographic Explorer handle the grade setting
      initialGradesSet.current = true; // Prevent this from running again
    }
  }, [selectedSchool, onGradeChange, onHasPreKChange]);

  const filterSize = sticky ? "small" : "medium";
  const filterHeight = sticky ? 40 : 56;

  const formatAddress = (school) => {
    if (!school) return '';
    
    const formattedStreetAddress = school.street_address
      .split(' ')
      .map(capitalizeWord)
      .join(' ');
  
    const formattedCity = school.city
      .split(' ')
      .map(capitalizeWord)
      .join(' ');
  
    return `${formattedStreetAddress}\n${formattedCity}, ${school.state} ${school.zip_code}`;
  };

  const renderFilters = () => (
    <Grid 
      container 
      spacing={2} 
      sx={{ 
        display: { 
          xs: 'grid',  // Grid layout on mobile
          md: 'flex'   // Original flex layout on desktop
        },
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',  // 2 columns on mobile
          sm: 'repeat(3, 1fr)',  // 3 columns on small screens
          md: 'none'             // Disable grid on desktop
        },
        gap: 2,
        overflowX: 'auto',      // Preserve horizontal scroll capability
        '& > .MuiGrid-item': {
          width: {
            xs: '100%',         // Full width in grid
            md: 'auto'          // Original width in flex
          }
        }
      }}
    >
      <Grid item style={{ 
        minWidth: 400,
        gridColumn: { xs: 'span 2', sm: 'span 2', md: 'auto' }
      }}>
        <Autocomplete
          size={filterSize}
          options={sortedSchools}
          getOptionLabel={(option) => formatSchoolName(option.name)}
          value={selectedSchool || null}
          onChange={(event, newValue) => {
            initialGradesSet.current = false;
            onSchoolChange({ target: { value: newValue?.uuid || '' } });
          }}
          isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
          filterOptions={filterSchoolOptions}
          renderOption={renderSchoolOption}
          renderInput={(params) => (
            <TextField
              {...params}
              label="School"
              sx={{ height: filterHeight }}
            />
          )}
        />
      </Grid>
  
      {showSchoolType && (
        <Grid item style={{ minWidth: 150, maxWidth: 200 }}>
          <FormControl fullWidth size={filterSize}>
            <InputLabel id="school-type-select-label">School Type</InputLabel>
            <Select
              labelId="school-type-select-label"
              id="school-type-select"
              multiple
              value={schoolTypeFilter}
              label="School Type"
              onChange={onSchoolTypeChange}
              input={<OutlinedInput label="School Type" />}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                // Show "All" if all available types are selected
                const availableTypes = availableSchoolTypes.filter(type => type !== 'All');
                const allSelected = availableTypes.length > 0 && 
                                   availableTypes.every(type => selected.includes(type));
                if (allSelected) {
                  return 'All';
                }
                // Always show the actual names
                return selected.map(type => formatSchoolType(type)).join(', ');
              }}
              sx={{ 
                height: filterHeight,
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              {availableSchoolTypes.filter(type => type !== 'All').map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={schoolTypeFilter.indexOf(type) > -1} />
                  <ListItemText primary={formatSchoolType(type)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
  
      <Grid item style={{ minWidth: 150 }}>
        <FormControl fullWidth size={filterSize}>
          <InputLabel id="governance-select-label">Governance</InputLabel>
          <Select
            labelId="governance-select-label"
            id="governance-select"
            multiple
            value={governanceFilter}
            label="Governance"
            onChange={onGovernanceChange}
            input={<OutlinedInput label="Governance" />}
            renderValue={(selected) => {
              if (selected.length === 0) return '';
              // Show "All" if all offered governance types are selected
              const allSelected = governanceOptions.every((type) => selected.includes(type));
              if (allSelected) {
                return 'All';
              }
              return selected.join(', ');
            }}
            sx={{ height: filterHeight }}
          >
            {governanceOptions.map((gov) => (
              <MenuItem key={gov} value={gov}>
                <Checkbox checked={governanceFilter.indexOf(gov) > -1} />
                <ListItemText primary={gov} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
  
      <Grid item style={{ minWidth: 150 }}>
        <FormControl fullWidth size={filterSize}>
          <InputLabel id="drive-time-select-label">Travel Time</InputLabel>
          <Select
            labelId="drive-time-select-label"
            id="drive-time-select"
            value={selectedDriveTime}
            label="Travel Time"
            onChange={onDriveTimeChange}
            sx={{ height: filterHeight }}
          >
            {driveTimeOptions.map((time) => (
              <MenuItem key={time} value={time}>
                {time} minutes
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {onTravelModeChange && (
        <Grid item style={{ minWidth: 180 }}>
          <FormControl fullWidth size={filterSize}>
            <InputLabel id="travel-mode-select-label">Mode</InputLabel>
            <Select
              labelId="travel-mode-select-label"
              id="travel-mode-select"
              value={selectedTravelMode}
              label="Mode"
              onChange={onTravelModeChange}
              sx={{ height: filterHeight }}
            >
              {travelModeOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
  
      <Grid item style={{ minWidth: 150, maxWidth: 200 }}>
        <FormControl fullWidth size={filterSize}>
          <InputLabel id="grade-select-label">Grades</InputLabel>
          <Select
            labelId="grade-select-label"
            id="grade-select"
            multiple
            value={selectedGrades.map(grade => 
              grade === 'Kindergarten' ? 'K' : grade
            )}
            onChange={(e) => {
              // Convert K back to Kindergarten for consistency with backend
              const selectedValues = e.target.value.map(grade => 
                grade === 'K' ? 'Kindergarten' : grade
              );
              
              // Sort them according to our rules
              const sortedGrades = [...selectedValues].sort((a, b) => {
                if (a === 'Kindergarten') return -1;
                if (b === 'Kindergarten') return 1;
                return parseInt(a) - parseInt(b);
              });

              onGradeChange({ target: { value: sortedGrades } });
            }}
            input={<OutlinedInput label="Grades" />}
            renderValue={(selected) => selected.sort((a, b) => {
              if (a === 'K') return -1;
              if (b === 'K') return 1;
              return parseInt(a) - parseInt(b);
            }).join(', ')}
            sx={{ height: filterHeight }}
          >
            {GRADES.map((grade) => (
              <MenuItem key={grade} value={grade}>
                <Checkbox 
                  checked={selectedGrades.includes(
                    grade === 'K' ? 'Kindergarten' : grade
                  )} 
                />
                <ListItemText primary={grade} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  if (compact) {
    return renderFilters();
  }

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <SchoolOutlined sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6">
              {selectedSchool ? formatSchoolName(selectedSchool.name) : 'Select a School'}
            </Typography>
          </Box>
          {selectedSchool && (
            <Typography variant="body2" color="text.secondary">
              {formatAddress(selectedSchool)}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md="auto">
          <Divider orientation="vertical" flexItem />
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Filters
          </Typography>
          {renderFilters()}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SharedFilters;